import { FormControl } from '@angular/forms';
import { IOptionItem } from '@ui-lib/components';
import { ScanResultOutcomeKind } from '../../core/openapi/bff-admin-api';

export interface ScanById {
  id: string;
  facility: ScanByIdFacility;
  containers: ScanByIdContainer[];
  containersCount: number;
  foundDevicesCount: number;
  devicesWithoutModelCount: number;
  scannedCount: number;
  totalCount: number;
  isApprovable: boolean;
  status: ScanByIdStatus;
  createdDate: string;
}

export interface ScanByIdContainer {
  id: string;
  name: string;
  ipRange: string;
}

export interface ScanByIdFacility {
  id: string;
  name: string;
}

export interface ScanByIdStatus {
  id: number;
  name: string;
}

export interface CreateScan {
  id: string;
}

export interface SearchScans {
  id: string;
  facility: SearchScansFacility;
  containersCount: number;
  scanResultsCount: number;
  createdByUser: SearchScansCreatedByUser;
  status: SearchScansStatus;
  createdDate: string;
}

export interface SearchScansFacility {
  id: string;
  name: string;
}

export interface SearchScansCreatedByUser {
  id: string;
  firstName: string;
  lastName: string;
}

export interface SearchScansStatus {
  id: number;
  name: string;
}

export interface IScannerFormFilters {
  facilityId: FormControl<IOptionItem | null>;
  datePeriod: FormControl<string[] | null>;
}

export interface IScannerDetailsFiltersForm {
  keyword: FormControl<string>;
  modelIds: FormControl<string[]>;
  statusIds: FormControl<number[]>;
}

export interface SearchScanResult {
  id: string;
  scanId: string;
  containerId?: string | null;
  containerName: string;
  ipAddress: string;
  scanResultId?: string | null;
  equipmentId?: string | null;
  macAddress?: string | null;
  model?: SearchScanResultsModel | null;
  referenceSpec?: ScanResultReference | null;
  rawModel?: string | null;
  firmware?: string | null;
  result: SearchScanResultOutcome;
  previousDevice?: SearchScanResultDevice | null;
  isFinalized: boolean;
  detectedAtUtc?: string | null;
  finalizedAtUtc?: string | null;
}

export interface SearchScanResultDevice {
  equipmentId?: string | null;
  ipAddress?: string | null;
  macAddress?: string | null;
  model?: SearchScanResultsModel | null;
  rawModel?: string | null;
  firmware?: string | null;
  detectedAtUtc?: string | null;
}

export interface SearchScanResultOutcome {
  id: number;
  kind: ScanResultOutcomeKind;
  name: string;
}

export interface SearchScanResultsModel {
  id: string;
  name: string;
  /** Паспортный номинал выбранной модели — с ним сравнивается найденный на устройстве эталон. */
  nominalHashrate: number | null;
  hashrateUnitName: string | null;
}

/**
 * Заводской эталон, снятый с устройства при сканировании.
 *
 * Непустое значение не гарантирует появление исполнения: оно ещё проходит серверные проверки
 * (совместимость единицы со шкалой модели, разумность диапазона, доверие к источнику).
 * Поэтому в интерфейсе формулируем как «на устройстве найдено значение», а не «будет создано
 * исполнение».
 */
export interface ScanResultReference {
  /** В базовых единицах (H/s либо Sol/s) — для показа человеку не годится. */
  nominalHashrate: number | null;
  /** То, что показываем. `null`, пока модель для результата не выбрана. */
  nominalHashrateInModelUnits: number | null;
  rawUnit: string | null;
  source: string | null;
}

type ScanResultBadgeModifier = 'success' | 'warning' | 'danger' | 'info' | 'secondary' | 'neutral';

export type ScanResultStatusBadge = {
  key: string;
  modifier: ScanResultBadgeModifier;
};

export enum ScanResultStatusId {
  NoChanges = 0,
  New = 1,
  Moved = 2,
  NotFound = 3,
  PendingProcessing = 4,
}

export const SCAN_RESULT_KIND_BY_STATUS_ID: Record<ScanResultStatusId, ScanResultOutcomeKind> = {
  [ScanResultStatusId.NoChanges]: ScanResultOutcomeKind.NoChanges,
  [ScanResultStatusId.New]: ScanResultOutcomeKind.New,
  [ScanResultStatusId.Moved]: ScanResultOutcomeKind.Moved,
  [ScanResultStatusId.NotFound]: ScanResultOutcomeKind.NotFound,
  [ScanResultStatusId.PendingProcessing]: ScanResultOutcomeKind.PendingProcessing,
};

export const SCAN_RESULT_BADGE: Record<ScanResultOutcomeKind, ScanResultStatusBadge> = {
  [ScanResultOutcomeKind.NoChanges]: {
    key: 'monitoring.scanner.details.statuses.no-changes',
    modifier: 'success',
  },
  [ScanResultOutcomeKind.New]: {
    key: 'monitoring.scanner.details.statuses.new',
    modifier: 'success',
  },
  [ScanResultOutcomeKind.Moved]: {
    key: 'monitoring.scanner.details.statuses.moved',
    modifier: 'secondary',
  },
  [ScanResultOutcomeKind.NotFound]: {
    key: 'monitoring.scanner.details.statuses.not-found',
    modifier: 'neutral',
  },
  [ScanResultOutcomeKind.PendingProcessing]: {
    key: 'monitoring.scanner.details.statuses.pending-processing',
    modifier: 'info',
  },
  // Аппарат не найден на своём месте, замены нет: остаётся в системе со статусом «Не найден».
  [ScanResultOutcomeKind.Offline]: {
    key: 'monitoring.scanner.details.statuses.offline',
    modifier: 'warning',
  },
};

export const FALLBACK_SCAN_RESULT_BADGE: ScanResultStatusBadge = {
  key: 'monitoring.scanner.details.statuses.pending-processing',
  modifier: 'info',
};

export enum ScanStatusId {
  Created = 1,
  Running,
  Paused,
  Completed,
  Failed,
  Cancelled,
  Approved,
}

type ScanBadgeModifier = 'success' | 'secondary' | 'error' | 'info' | 'warning';

export type ScanStatusBadge = {
  key: string;
  modifier?: ScanBadgeModifier;
};

export const SCAN_STATUS_BADGE: Record<ScanStatusId, ScanStatusBadge> = {
  [ScanStatusId.Created]: { key: 'monitoring.scanner.statuses.created', modifier: 'error' },
  [ScanStatusId.Running]: { key: 'monitoring.scanner.statuses.running', modifier: 'secondary' },
  [ScanStatusId.Paused]: { key: 'monitoring.scanner.statuses.paused', modifier: 'warning' },
  [ScanStatusId.Completed]: { key: 'monitoring.scanner.statuses.completed', modifier: 'success' },
  [ScanStatusId.Failed]: { key: 'monitoring.scanner.statuses.failed', modifier: 'error' },
  [ScanStatusId.Cancelled]: { key: 'monitoring.scanner.statuses.cancelled', modifier: 'info' },
  [ScanStatusId.Approved]: { key: 'monitoring.scanner.statuses.approved', modifier: 'success' },
};

export const FALLBACK_SCAN_STATUS_BADGE: ScanStatusBadge = {
  key: 'monitoring.scanner.statuses.created',
};
